/**
 * Shared query config for the event dashboard.
 *
 * The backend exposes a single `/dashboard` endpoint that returns all stats at once.
 * Multiple UI surfaces (Revenue, Orders, Tickets Sold, Guest Check-ins, alerts, etc.)
 * consume different slices of that payload.
 *
 * All dashboard hooks MUST spread these options so they share the same `queryKey`.
 * React Query then performs one network request and one poll timer, while each hook
 * can still expose its own shape via `select` (see hooks/useEventDashboardRevenue.ts).
 */
import { queryOptions } from '@tanstack/react-query';

import { DASHBOARD_STATS_REFETCH_INTERVAL_MS, queryKeys } from 'react-query';

import { fetchEventDashboardStats, type EventDashboardStats } from './api/fetchEventDashboardStats';

/** Normalized Total / Online / In-Person shape used by the four stat cards. */
export type EventDashboardStatCard = {
    total: number;
    online: number;
    inPerson: number;
};

type EventDashboardStatsQueryParams = {
    eventId: number | undefined;
    timezone: string;
};

/**
 * Base options for every dashboard hook. Spread this, then add a `select` when the
 * consumer only needs a subset of the response.
 */
export function eventDashboardStatsQueryOptions({ eventId, timezone }: EventDashboardStatsQueryParams) {
    return queryOptions({
        queryKey: queryKeys.eventDashboard.stats(eventId ?? 0, timezone),
        queryFn: () => fetchEventDashboardStats(eventId!, timezone),
        enabled: Boolean(eventId),
        refetchInterval: DASHBOARD_STATS_REFETCH_INTERVAL_MS,
    });
}

/** Maps the full dashboard payload to the Revenue card fields. */
export function selectRevenueStats(stats: EventDashboardStats): EventDashboardStatCard {
    return {
        total: stats.total_revenue + stats.pos_total_revenue,
        online: stats.total_revenue,
        inPerson: stats.pos_total_revenue,
    };
}

/** Maps the full dashboard payload to the Orders card fields. */
export function selectOrdersStats(stats: EventDashboardStats): EventDashboardStatCard {
    return {
        total: stats.total_orders + stats.pos_total_orders,
        online: stats.total_orders,
        inPerson: stats.pos_total_orders,
    };
}

/** Maps the full dashboard payload to the Tickets Sold card fields. */
export function selectTicketsSoldStats(stats: EventDashboardStats): EventDashboardStatCard {
    return {
        total: stats.total_tickets + stats.pos_total_tickets,
        online: stats.total_tickets,
        inPerson: stats.pos_total_tickets,
    };
}

/** Maps the full dashboard payload to the Guest Check-ins card fields. */
export function selectGuestCheckinsStats(stats: EventDashboardStats): EventDashboardStatCard {
    return {
        total: stats.scan_count + stats.pos_scan_count,
        online: stats.scan_count,
        inPerson: stats.pos_scan_count,
    };
}
